import {
  Color3,
  MeshBuilder,
  Scene,
  StandardMaterial,
  TransformNode,
} from '@babylonjs/core';

/**
 * Subset of VehicleTuning needed to build the visual chassis + wheel meshes. Defined as a
 * standalone interface so traffic cars (which carry their own tuning) and the player car
 * (which carries VehicleTuning) can both share this factory.
 */
export interface ChassisDimensions {
  chassisWidth: number;
  chassisHeight: number;
  chassisLength: number;
  wheelRadius: number;
  wheelWidth: number;
}

/**
 * Build the visual chassis (body + cabin) as a parented TransformNode. Returns the root so
 * the caller can parent it under the rigid-body root and let syncVisual() pose it each frame.
 *
 * `bodyColor` lets each car pick its own paint job — the player keeps the original red, and
 * each traffic car picks a random muted color from the palette in traffic/tuning.ts.
 *
 * `idSuffix` is appended to mesh/material names so several cars in the same scene don't
 * collide on Babylon's name registry. Player passes nothing → default suffix; traffic cars
 * pass their unique id.
 */
export function buildVehicleChassis(
  scene: Scene,
  dims: ChassisDimensions,
  bodyColor: Color3,
  idSuffix = '',
): TransformNode {
  const suffix = idSuffix ? `-${idSuffix}` : '';
  const root = new TransformNode(`chassis-visual${suffix}`, scene);

  const body = MeshBuilder.CreateBox(
    `chassis-body${suffix}`,
    {
      width: dims.chassisWidth,
      height: dims.chassisHeight,
      depth: dims.chassisLength,
    },
    scene,
  );
  body.parent = root;
  body.position.y = 0; // already centered on rigid body
  const bodyMat = new StandardMaterial(`chassis-mat${suffix}`, scene);
  bodyMat.diffuseColor = bodyColor;
  bodyMat.specularColor = new Color3(0.2, 0.2, 0.2);
  body.material = bodyMat;

  // Cabin "greenhouse" on top — same dark-glass color for every car, makes the body color
  // pop and keeps the silhouette consistent.
  const cabin = MeshBuilder.CreateBox(
    `chassis-cabin${suffix}`,
    {
      width: dims.chassisWidth * 0.85,
      height: dims.chassisHeight * 0.6,
      depth: dims.chassisLength * 0.45,
    },
    scene,
  );
  cabin.parent = root;
  cabin.position.set(0, dims.chassisHeight * 0.55, -dims.chassisLength * 0.05);
  const cabinMat = new StandardMaterial(`cabin-mat${suffix}`, scene);
  cabinMat.diffuseColor = new Color3(0.15, 0.16, 0.2);
  cabin.material = cabinMat;
  return root;
}

/**
 * Build a single wheel mesh. The cylinder defaults to a Y-axis spin; we pre-rotate so it
 * spins around X (forward-facing wheel) and bake the transform so syncVisual() can apply
 * pure local rotations without fighting the pre-rotation.
 */
export function buildWheelMesh(
  scene: Scene,
  dims: ChassisDimensions,
  name: string,
): TransformNode {
  const cyl = MeshBuilder.CreateCylinder(
    name,
    {
      diameter: dims.wheelRadius * 2,
      height: dims.wheelWidth,
      tessellation: 18,
    },
    scene,
  );
  cyl.rotation.z = Math.PI / 2;
  cyl.bakeCurrentTransformIntoVertices();

  const mat = new StandardMaterial(`${name}-mat`, scene);
  mat.diffuseColor = new Color3(0.07, 0.07, 0.07);
  cyl.material = mat;
  return cyl;
}
